import { useEffect } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Statistic,
  Switch,
  Table,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

interface RewardAnalytics {
  total: number;
  available: number;
  claimed: number;
  expired: number;
  void: number;
  claimRate: number;
  coinsDistributed: number;
  byType: { type: string; total: number; claimed: number }[];
}

interface GiftForm {
  userId: string;
  coins: number;
  title: string;
  description?: string;
  isScratchCard?: boolean;
}

interface RewardConfig {
  scratchByDefault: boolean;
  defaultExpiryDays: number;
}

/** Reward Center analytics + manual gift. Admin / ops / finance. */
export function RewardsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<GiftForm>();

  const [cfgForm] = Form.useForm<RewardConfig>();

  const analytics = useQuery({
    queryKey: ['admin', 'rewards', 'analytics'],
    queryFn: async () => (await api.get<RewardAnalytics>('/admin/rewards/analytics')).data,
  });

  const config = useQuery({
    queryKey: ['admin', 'rewards', 'config'],
    queryFn: async () => (await api.get<RewardConfig>('/admin/rewards/config')).data,
  });
  useEffect(() => {
    if (config.data) cfgForm.setFieldsValue(config.data);
  }, [config.data, cfgForm]);

  const saveConfig = useMutation({
    mutationFn: async (v: RewardConfig) => (await api.post<RewardConfig>('/admin/rewards/config', v)).data,
    onSuccess: (data) => {
      message.success('Reward settings updated.');
      cfgForm.setFieldsValue(data);
      qc.invalidateQueries({ queryKey: ['admin', 'rewards', 'config'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed',
      ),
  });

  const gift = useMutation({
    mutationFn: async (v: GiftForm) => (await api.post('/admin/rewards/gift', v)).data,
    onSuccess: () => {
      message.success('Gift reward created — the user can claim it in their Reward Center.');
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'rewards', 'analytics'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Failed',
      ),
  });

  const a = analytics.data;
  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>Reward Center</Typography.Title>
      <Typography.Paragraph type="secondary">
        Every promo (referral, coupon, gift, scratch card) is a claimable reward — coins hit the
        wallet only on claim.
      </Typography.Paragraph>

      <Card loading={analytics.isLoading} style={{ marginBottom: 20 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}><Statistic title="Total" value={a?.total ?? 0} /></Col>
          <Col xs={12} sm={8} md={6}><Statistic title="Available" value={a?.available ?? 0} /></Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="Claimed" value={a?.claimed ?? 0} valueStyle={{ color: '#10B981' }} />
          </Col>
          <Col xs={12} sm={8} md={6}><Statistic title="Expired" value={a?.expired ?? 0} /></Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="Coins distributed" value={a?.coinsDistributed ?? 0} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="Claim rate" value={((a?.claimRate ?? 0) * 100).toFixed(1)} suffix="%" />
          </Col>
        </Row>
      </Card>

      <Card title="Settings" loading={config.isLoading} style={{ maxWidth: 640, marginBottom: 20 }}>
        <Form<RewardConfig> form={cfgForm} layout="vertical" onFinish={(v) => saveConfig.mutate(v)}>
          <Form.Item
            name="scratchByDefault"
            label="Referral & coupon rewards as scratch cards"
            valuePropName="checked"
            tooltip="When on, referral and coupon rewards arrive as scratch cards (hidden amount, tap to reveal)."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="defaultExpiryDays"
            label="Default reward expiry (days)"
            rules={[{ required: true, type: 'number', min: 1, max: 365 }]}
          >
            <InputNumber min={1} max={365} style={{ width: 200 }} addonAfter="days" />
          </Form.Item>
          <Can permission="rewards.edit">
            <Button type="primary" htmlType="submit" loading={saveConfig.isPending}>
              Save settings
            </Button>
          </Can>
        </Form>
      </Card>

      <Row gutter={20}>
        <Col xs={24} md={14}>
          <Card title="By type" loading={analytics.isLoading}>
            <Table
              rowKey="type"
              size="small"
              pagination={false}
              dataSource={a?.byType ?? []}
              columns={[
                { title: 'Type', dataIndex: 'type', key: 'type' },
                { title: 'Total', dataIndex: 'total', key: 'total' },
                { title: 'Claimed', dataIndex: 'claimed', key: 'claimed' },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card title="Manual gift">
            <Form<GiftForm> form={form} layout="vertical" onFinish={(v) => gift.mutate(v)}>
              <Form.Item name="userId" label="User ID" rules={[{ required: true, len: 36 }]}>
                <Input placeholder="user uuid" />
              </Form.Item>
              <Form.Item name="coins" label="Coins" rules={[{ required: true, type: 'number', min: 1 }]}>
                <InputNumber min={1} max={1000000} style={{ width: '100%' }} addonAfter="coins" />
              </Form.Item>
              <Form.Item name="title" label="Title" rules={[{ required: true, max: 80 }]}>
                <Input placeholder="e.g. Loyalty gift" />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} maxLength={200} />
              </Form.Item>
              <Form.Item
                name="isScratchCard"
                label="Scratch card"
                valuePropName="checked"
                tooltip="Issue as a scratch card — coins are hidden until the user taps to scratch (which claims it)."
              >
                <Switch />
              </Form.Item>
              <Can permission="rewards.gift">
                <Button type="primary" htmlType="submit" loading={gift.isPending}>
                  Create gift
                </Button>
              </Can>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
